// =============================================================================
// settings-logic.ts – Pure settings computation (no DOM)
// =============================================================================

import { TRAIN_STATIONS } from "../data/trains.js";
import { LINE_CONFIG } from "./line-config.js";
import type { Settings, AppConfig } from "./types.js";

// ─── Helpers ────────────────────────────────────────────────────────────────

export function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function buildDefaultSettings(cfg: AppConfig): Settings {
  return {
    ...cfg.defaults,
    favoriteStops: structuredClone(cfg.favoriteStops || {}),
    timetableStops: {},
    visibleStops: {},
    followedLinesByCity: {}
  } as Settings;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== (b as unknown[]).length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], (b as unknown[])[i])) return false;
    }
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  const bk = Object.keys(bo);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(bo, k)) return false;
    if (!deepEqual(ao[k], bo[k])) return false;
  }
  return true;
}

// ─── sanitizeSettings ───────────────────────────────────────────────────────

export function sanitizeSettings(
  raw: Partial<Settings> | null | undefined,
  cfg: AppConfig
): Settings {
  const defaults = buildDefaultSettings(cfg);
  const settings: Record<string, unknown> = { ...defaults, ...(raw || {}) };
  settings.focusCity =
    raw?.focusCity && cfg.focusCities?.[raw.focusCity]
      ? raw.focusCity
      : cfg.defaults.focusCity || "BT";
  settings.walkRossini = clampNumber(settings.walkRossini, 1, 30, cfg.defaults.walkRossini);
  settings.largeText = !!raw?.largeText;
  settings.maxTransfers = clampNumber(raw?.maxTransfers, 1, 2, 1);

  // Determine Canegrate FS drive time: if legacy driveCanegrate exists, migrate it
  let driveVal: number;
  if (raw?.driveCanegrate !== undefined && raw.driveCanegrate !== null) {
    driveVal = clampNumber(raw.driveCanegrate, 1, 45, 16);
  } else if (raw?.stationReachMinutes?.CN_FS !== undefined && raw.stationReachMinutes.CN_FS !== null) {
    driveVal = clampNumber(raw.stationReachMinutes.CN_FS, 1, 45, 16);
  } else {
    const defaultCnFs = cfg.defaults.stationReachMinutes?.CN_FS !== undefined
      ? cfg.defaults.stationReachMinutes.CN_FS
      : 16;
    driveVal = clampNumber(defaultCnFs, 1, 45, 16);
  }

  let tight = clampNumber(
    settings.connectionTightMin,
    1,
    20,
    ((cfg.defaults as Record<string, unknown>).connectionTightMin as number) || 4
  );
  let good = clampNumber(
    settings.connectionGoodMin,
    5,
    45,
    ((cfg.defaults as Record<string, unknown>).connectionGoodMin as number) || 12
  );
  let long = clampNumber(
    settings.connectionLongMin,
    10,
    90,
    ((cfg.defaults as Record<string, unknown>).connectionLongMin as number) || 25
  );

  if (!(tight < good && good < long)) {
    tight = ((cfg.defaults as Record<string, unknown>).connectionTightMin as number) || 4;
    good = ((cfg.defaults as Record<string, unknown>).connectionGoodMin as number) || 12;
    long = ((cfg.defaults as Record<string, unknown>).connectionLongMin as number) || 25;
  }

  settings.connectionTightMin = tight;
  settings.connectionGoodMin = good;
  settings.connectionLongMin = long;

  const stationReachMinutes: Record<string, number> = {};
  const ALL_STATIONS = Object.keys(TRAIN_STATIONS as Record<string, unknown>);
  ALL_STATIONS.forEach((station) => {
    let rawVal = (raw as Record<string, unknown>)?.stationReachMinutes
      ? ((raw as Record<string, unknown>).stationReachMinutes as Record<string, unknown>)?.[station]
      : undefined;
    if (station === "CN_FS" && rawVal === undefined) {
      rawVal = driveVal;
    }
    const val = rawVal !== undefined ? rawVal : 0;
    stationReachMinutes[station] = clampNumber(val, 0, 60, 0);
  });
  
  if (raw?.stationReachMinutes?.CN_FS !== undefined) {
    stationReachMinutes.CN_FS = clampNumber(raw.stationReachMinutes.CN_FS, 0, 60, 0);
  } else {
    stationReachMinutes.CN_FS = clampNumber(driveVal, 0, 60, 16);
  }
  
  settings.stationReachMinutes = stationReachMinutes;
  settings.driveCanegrate = clampNumber(stationReachMinutes.CN_FS, 1, 45, 16);

  settings.liveDirection = settings.liveDirection === "return" ? "return" : "outbound";

  const CITY_MAIN_STATION_CODE: Record<string, string> = {
    BT: "CN_FS",
    VC: "CN_FS",
    DG: "LG_FS",
    AC: "LG_FS",
    CZ: "PB_FS",
    LG: "LG_FS",
    PB: "PB_FS",
    BS: "BS_FS"
  };
  const focusCityCode = (settings.focusCity as string) || "BT";
  const mainStation = CITY_MAIN_STATION_CODE[focusCityCode] || "CN_FS";
  settings.visibleTrains = Array.isArray(raw?.visibleTrains) ? raw!.visibleTrains : [mainStation];
  settings.liveHero = typeof raw?.liveHero === "string" ? raw.liveHero : "Z649";

  // Dynamic favorite stops merge
  const activeFocusStops =
    cfg.focusCities?.[settings.focusCity as string]?.favoriteStops || cfg.favoriteStops || {};
  const mergedFavoriteStops: Record<string, Record<string, string>> = structuredClone(
    activeFocusStops
  ) as Record<string, Record<string, string>>;
  const userFavoriteStops = (settings.favoriteStops || {}) as Record<
    string,
    Record<string, string>
  >;
  for (const lineId of Object.keys(userFavoriteStops)) {
    if (!mergedFavoriteStops[lineId]) {
      mergedFavoriteStops[lineId] = { ...userFavoriteStops[lineId] };
    } else {
      mergedFavoriteStops[lineId] = {
        ...mergedFavoriteStops[lineId],
        ...userFavoriteStops[lineId]
      };
    }
  }
  settings.favoriteStops = mergedFavoriteStops;

  settings.invertDirections = !!raw?.invertDirections;
  settings.timetableStops =
    settings.timetableStops && typeof settings.timetableStops === "object"
      ? settings.timetableStops
      : {};
  // P1: validate persisted timetableStops — strip entries pointing to lines
  // not present in LINE_CONFIG. (S3 already prevents writing new garbage, but
  // pre-fix users may have poisoned entries that survived upgrade.) Full
  // stop-code validation runs at runtime once the line's data is loaded.
  const knownLineIds = new Set(Object.keys(LINE_CONFIG));
  const timetableStops = settings.timetableStops as Record<string, unknown>;
  for (const lineId of Object.keys(timetableStops)) {
    if (!knownLineIds.has(lineId)) delete timetableStops[lineId];
  }
  settings.timetableStops = timetableStops;
  settings.visibleStops =
    settings.visibleStops && typeof settings.visibleStops === "object" ? settings.visibleStops : {};
  settings.followedLinesByCity =
    raw?.followedLinesByCity && typeof raw.followedLinesByCity === "object"
      ? raw.followedLinesByCity
      : {};

  if (raw?.userProfile && typeof raw.userProfile === "object") {
    settings.userProfile = raw.userProfile;
  } else if (settings.userProfile && typeof settings.userProfile !== "object") {
    delete settings.userProfile;
  }
  return settings as unknown as Settings;
}
